/** Cliente da API. Fino de propósito: sem cache, sem estado. */

import type {
  AuditEntry,
  Briefing,
  Call,
  CallSummary,
  Case,
  DryRunReply,
  PendingHandoff,
  PipelineMetrics,
  PipelineSession,
  PipelineStatus,
  PipelineTurnResult,
  Policy,
  PolicyEnvelope,
  PolicyVersion,
  RealtimeSession,
  RealtimeStatus,
  Scenario,
  ScriptResponse,
  ToolExecutionResponse,
} from './types'

import { clearToken, getToken } from './auth'
import { withBase } from './basePath'

// Respeita o prefixo de deploy: na raiz vira `/api`, sob prefixo
// `/stratumtech/api`.
const BASE = withBase('api')

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Cabeçalhos de toda chamada à nossa API.
 *
 * Existe como função porque o TTS não passa pelo `request` (devolve áudio
 * binário, não JSON) — e esquecer o token lá deixou o TTS tomando 401 em
 * produção enquanto o resto funcionava. Um lugar só evita a próxima omissão.
 */
function authHeaders(): Record<string, string> {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/** Sessão inválida (senha trocada, serviço reiniciado): volta ao login. */
function bounceToLoginOn401(status: number, path: string): void {
  // O próprio login fica de fora para o erro de senha aparecer na tela.
  if (status === 401 && !path.startsWith('/auth/')) {
    clearToken()
    window.location.href = withBase('login')
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...init?.headers },
  })

  bounceToLoginOn401(response.status, path)

  if (!response.ok) {
    let detail: unknown
    let message = `${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      detail = body?.detail ?? body
      if (typeof detail === 'string') message = detail
      else if (detail && typeof detail === 'object' && 'message' in detail) {
        message = String((detail as { message: unknown }).message)
      }
    } catch {
      // Resposta sem corpo JSON — mantém a mensagem de status.
    }
    throw new ApiError(response.status, message, detail)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  health: () => request<{ status: string; realtime_enabled: boolean }>('/health'),

  // autenticação
  authStatus: () => request<{ required: boolean }>('/auth/status'),
  authLogin: (password: string) =>
    request<{ token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // política
  getDraft: () => request<PolicyEnvelope>('/policy/draft'),
  saveDraft: (policy: Policy) =>
    request<PolicyEnvelope>('/policy/draft', { method: 'PUT', body: JSON.stringify(policy) }),
  getPublished: () => request<Policy>('/policy/published'),
  listVersions: () => request<PolicyVersion[]>('/policy/versions'),
  dryRun: (message: string, caseId = 'maria-fernandes') =>
    request<DryRunReply>('/policy/draft/dry-run', {
      method: 'POST',
      body: JSON.stringify({ message, case_id: caseId }),
    }),
  dryRunSuggestions: () => request<string[]>('/policy/draft/dry-run/suggestions'),
  publish: (justification: string, approver: string) =>
    request<{ version: PolicyVersion; next_draft_version: string }>('/policy/publish', {
      method: 'POST',
      body: JSON.stringify({ justification, approver }),
    }),

  // casos e cenários
  listCases: () => request<Case[]>('/cases'),
  listScenarios: () => request<Scenario[]>('/scenarios'),
  briefing: (caseId: string, scenarioId: string) =>
    request<Briefing>(`/briefing?case_id=${caseId}&scenario_id=${scenarioId}`),
  script: (scenarioId: string) => request<ScriptResponse>(`/scenarios/${scenarioId}/script`),

  // chamadas
  createCall: (caseId: string, scenarioId: string, source: string) =>
    request<Call>('/calls', {
      method: 'POST',
      body: JSON.stringify({ case_id: caseId, scenario_id: scenarioId, source }),
    }),
  /** Lê uma chamada criada na linha, para o console espelhá-la. */
  createCallMirror: (callId: string) => request<Call>(`/calls/${callId}`),
  executeTool: (callId: string, name: string, args: Record<string, unknown>, at: string) =>
    request<ToolExecutionResponse>(`/calls/${callId}/tools`, {
      method: 'POST',
      body: JSON.stringify({ name, arguments: args, at }),
    }),
  appendAudit: (callId: string, event: string, at: string) =>
    request<AuditEntry>(`/calls/${callId}/audit`, {
      method: 'POST',
      body: JSON.stringify({ event, at }),
    }),
  endCall: (callId: string, payload: Record<string, unknown>) =>
    request<CallSummary>(`/calls/${callId}/end`, { method: 'POST', body: JSON.stringify(payload) }),

  // realtime
  realtimeStatus: () => request<RealtimeStatus>('/realtime/status'),
  /** Com o caso, o prompt sobe com {{nome}} e afins resolvidos. */
  realtimeSession: (caseId?: string) =>
    request<RealtimeSession>('/realtime/session', {
      method: 'POST',
      body: JSON.stringify({ case_id: caseId ?? null }),
    }),

  // pipeline (Modelo B)
  pipelineStatus: () => request<PipelineStatus>('/pipeline/status'),
  pipelineSession: () => request<PipelineSession>('/pipeline/session', { method: 'POST' }),
  pipelineRespond: (callId: string, text: string, role: 'user' | 'system', at: string) =>
    request<PipelineTurnResult>(`/pipeline/calls/${callId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ text, role, at }),
    }),
  /**
   * Fala em PCM streaming — devolve a Response crua para o chamador consumir
   * o body chunk a chunk (fora do `request`, que assume JSON).
   */
  pipelineTtsStream: async (text: string, callId?: string): Promise<Response> => {
    const response = await fetch(`${BASE}/pipeline/tts`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ text, call_id: callId ?? null }),
    })
    bounceToLoginOn401(response.status, '/pipeline/tts')
    if (!response.ok) {
      throw new ApiError(response.status, `TTS falhou (${response.status} ${response.statusText})`)
    }
    return response
  },
  pipelineMetrics: () => request<PipelineMetrics>('/pipeline/metrics'),
  pipelineFillers: () => request<{ fillers: string[] }>('/pipeline/fillers'),
  /** Ligação mais recente movida para o WhatsApp pela ferramenta continuar_whatsapp. */
  pipelineHandoff: () => request<{ handoff: PendingHandoff | null }>('/pipeline/handoff'),
}
